import { create } from 'zustand';

interface FileMetadata {
  id: string;
  name: string;
  size: number;
  type: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: Array<{
    fileName: string;
    page?: string;
    snippet: string;
  }>;
}

interface ChatDocsState {
  sessionId: string;
  vectorStoreId: string | null;
  files: FileMetadata[];
  messages: Message[];
  isStreaming: boolean;
  uploadProgress: { [fileId: string]: number };
  isUploading: boolean;
  
  // Actions
  setSessionId: (id: string) => void;
  setVectorStoreId: (id: string) => void;
  addFile: (file: FileMetadata) => void;
  removeFile: (id: string) => void;
  addMessage: (msg: Message) => void;
  updateMessage: (index: number, updates: Partial<Message>) => void;
  setStreaming: (streaming: boolean) => void;
  setUploadProgress: (fileId: string, progress: number) => void;
  setUploading: (uploading: boolean) => void;
  clearChat: () => void;
}

export const useChatDocsStore = create<ChatDocsState>((set) => ({
  sessionId: crypto.randomUUID(),
  vectorStoreId: null,
  files: [],
  messages: [],
  isStreaming: false,
  uploadProgress: {},
  isUploading: false,
  
  setSessionId: (id) => set({ sessionId: id }),
  setVectorStoreId: (id) => set({ vectorStoreId: id }),
  addFile: (file) => set((state) => ({ 
    files: [...state.files, {
      ...file,
      type: file.type || 'application/octet-stream'
    }] 
  })),
  removeFile: (id) => set((state) => ({ files: state.files.filter(f => f.id !== id) })),
  addMessage: (msg) => set((state) => ({ messages: [...state.messages, msg] })),
  updateMessage: (index, updates) => set((state) => ({
    messages: state.messages.map((m, i) => i === index ? { ...m, ...updates } : m)
  })),
  setStreaming: (streaming) => set({ isStreaming: streaming }),
  setUploadProgress: (fileId, progress) => set((state) => ({
    uploadProgress: { ...state.uploadProgress, [fileId]: progress }
  })),
  setUploading: (uploading) => set({ isUploading: uploading }),
  clearChat: () => set({ 
    messages: [], 
    files: [], 
    vectorStoreId: null,
    sessionId: crypto.randomUUID()
  })
}));

// Map global para mapeamento file_id → filename (compartilhado com backend via props)
export const fileNameById = new Map<string, string>();
